// The move table's JSON Schema (decision 0061: content as JSON with schemas). The package owns a move's shape and its
// ranges; the game adds its own fields (an id is required; a weapon, a label) and writes the schema into its content
// folder, the way @rpg/figure's card schema is generated (spike 002's `content:gen`).

/** A JSON Schema object. */
export type Schema = Record<string, unknown>;

const int = (minimum: number, maximum: number, description?: string): Schema => ({ type: "integer", minimum, maximum, ...(description ? { description } : {}) });
const num = (minimum: number, maximum: number, description?: string): Schema => ({ type: "number", minimum, maximum, ...(description ? { description } : {}) });
const pair = (item: Schema, description?: string): Schema => ({ type: "array", items: item, minItems: 2, maxItems: 2, ...(description ? { description } : {}) });
const obj = (properties: Record<string, Schema>, required: string[], description?: string): Schema => ({
  type: "object",
  additionalProperties: false,
  required,
  properties,
  ...(description ? { description } : {}),
});
const STEPS = int(0, 1200);
const INTENTS = { enum: ["attack", "skill", "big", "dodge", "block", "blockRelease"] };

const SHAPE: Schema = {
  oneOf: [
    obj({ kind: { const: "circle" }, radius: num(0.1, 30) }, ["kind", "radius"]),
    obj({ kind: { const: "cone" }, radius: num(0.1, 30), angle: num(0.05, 6.3, "Full angle, rad") }, ["kind", "radius", "angle"]),
    obj({ kind: { const: "line" }, length: num(0.1, 40), width: num(0.05, 10) }, ["kind", "length", "width"]),
  ],
};

const MOVE_PROPS: Record<string, Schema> = {
  kind: { enum: ["light", "heavy", "skill", "big"] },
  windup: int(0, 1200, "Steps before the hit volume is live"),
  active: int(1, 1200, "Steps the hit volume is live"),
  recovery: int(0, 1200, "Steps after"),
  next: { type: "string", description: "The combo's next move id" },
  chainFrom: int(0, 1200, "From this step (of this move) an attack press chains into `next`"),
  cancels: {
    type: "array",
    maxItems: 8,
    items: obj({ from: STEPS, to: STEPS, into: { type: "array", items: INTENTS, minItems: 1, maxItems: 6 }, onHit: { type: "boolean" } }, ["from", "into"]),
  },
  power: num(0, 50, "The ability multiplier on attack"),
  poise: num(0, 1000, "Poise removed from the target"),
  severity: num(0, 1),
  hitStop: num(0, 0.5, "Real seconds of hit-stop on contact"),
  hit: obj(
    {
      radius: num(0.05, 20),
      height: num(-2, 10, "Above the attacker's feet, m"),
      reach: pair(num(0, 60), "Reach at the active window's start and end, m"),
      arc: pair(num(-6.3, 6.3), "Angle from the facing at the start and end, rad"),
      anchor: { enum: ["self", "target"], description: "Measured from the attacker's feet (default) or where its target stood when the move started" },
    },
    ["radius", "height", "reach", "arc"],
  ),
  lunge: obj({ speed: num(0, 40), from: STEPS, to: STEPS, stopAt: num(0, 10) }, ["speed", "from", "to", "stopAt"]),
  armour: pair(STEPS, "Steps in which poise can't break"),
  unblockable: { type: "boolean" },
  telegraph: obj({ shape: SHAPE, style: { type: "string" }, offset: num(-10, 30) }, ["shape", "style"]),
  charge: num(0, 100, "Big-move charge per landed hit"),
  damage: { type: "array", items: { type: "string" }, maxItems: 6, description: "Damage-type tags" },
  applies: {
    type: "array",
    maxItems: 4,
    description: "Effects a landed hit applies to the target (the ability set's ids: burn, slow, stun)",
    items: obj({ effect: { type: "string", minLength: 1 }, chance: num(0, 1, "Default 1") }, ["effect"]),
  },
};

/** The move's JSON Schema; `extra` adds the game's own properties (an `id` is always required). */
export function moveSchema(extra: { properties?: Record<string, Schema>; required?: string[]; title?: string; description?: string } = {}): Schema {
  return {
    $schema: "http://json-schema.org/draft-07/schema#",
    title: extra.title ?? "Move",
    ...(extra.description ? { description: extra.description } : {}),
    type: "object",
    additionalProperties: false,
    required: [...new Set([...(extra.required ?? []), "id", "kind", "windup", "active", "recovery", "power", "poise", "severity", "hitStop", "hit"])],
    properties: { id: { type: "string" }, ...(extra.properties ?? {}), ...MOVE_PROPS },
  };
}
